import React, { useState, useEffect } from 'react';
import { FileSpreadsheet, Search, Download, Calendar, X, ArrowLeft, Filter, Bot, RotateCcw, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import ChipDumpingTab from './ChipDumpingTab';
import CollusionTab from './CollusionTab';
import RetencionTab from './RetencionTab';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PPSRInforme() {
  const [loading, setLoading] = useState(false);
  
  // Filtros (PPSR fijo)
  const tipo = 'PPSR'; // Siempre PPSR
  const [snapshots, setSnapshots] = useState([]);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [allUniones, setAllUniones] = useState([]);
  const [selectedUnionId, setSelectedUnionId] = useState('');
  const [allClubs, setAllClubs] = useState([]);
  const [selectedClubId, setSelectedClubId] = useState('');
  const [modalidades, setModalidades] = useState([]);
  const [selectedModalidades, setSelectedModalidades] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [tipoBusquedaPPSR, setTipoBusquedaPPSR] = useState('jugador');
  const [busquedaClubIdPPSR, setBusquedaClubIdPPSR] = useState('');
  const [busquedaUnionIdPPSR, setBusquedaUnionIdPPSR] = useState('');
  const [resultadoClubPPSR, setResultadoClubPPSR] = useState(null);
  const [resultadoUnionPPSR, setResultadoUnionPPSR] = useState(null);
  const [loadingBusquedaPPSR, setLoadingBusquedaPPSR] = useState(false);
  
  // Datos mostrados
  const [clubs, setClubs] = useState([]);
  const [jugadores, setJugadores] = useState([]);
  const [hasFilters, setHasFilters] = useState(false);
  
  // Modal de mesa
  const [showMesaModal, setShowMesaModal] = useState(false);
  const [mesaSeleccionada, setMesaSeleccionada] = useState(null);
  const [jugadoresMesa, setJugadoresMesa] = useState([]);
  const [loadingMesa, setLoadingMesa] = useState(false);
  
  // Filtros del modal de mesa
  const [filtroClubMesa, setFiltroClubMesa] = useState('');
  const [filtroJugadorMesa, setFiltroJugadorMesa] = useState('');
  
  // Buscador de mesas
  const [busquedaMesaId, setBusquedaMesaId] = useState('');
  
  // Rol del usuario
  const userStored = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = userStored.role !== 'worker';

  // Control Bot
  const [loadingControlBot, setLoadingControlBot] = useState(false);
  
  // Análisis Resultados Bot
  const [archivoResultados, setArchivoResultados] = useState(null);
  const [semanaInicioBot, setSemanaInicioBot] = useState('');
  const [semanaFinBot, setSemanaFinBot] = useState('');
  const [incluyePerdedores, setIncluyePerdedores] = useState(false);
  const [loadingProcesar, setLoadingProcesar] = useState(false);
  const [historialInformes, setHistorialInformes] = useState([]);
  const [activeTab, setActiveTab] = useState('informe');
  const [agenciaInfo, setAgenciaInfo] = useState(null);
  const [agenciasList, setAgenciasList] = useState([]);


  useEffect(() => {
    loadSnapshots();
  }, []);

  const loadSnapshots = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/superunion/snapshots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const filtered = response.data.snapshots.filter(s => s.tipo === tipo);
      setSnapshots(filtered);
      
      // Cargar datos iniciales
      if (filtered.length > 0) {
        // Establecer rango de fechas inicial (última semana)
        const fechas = filtered.map(s => s.fecha).sort();
        if (fechas.length > 0) {
          setFechaHasta(fechas[fechas.length - 1]);
          if (fechas.length >= 7) {
            setFechaDesde(fechas[fechas.length - 7]);
          } else {
            setFechaDesde(fechas[0]);
          }
        }
        
        // Cargar uniones y clubs de todos los snapshots
        loadInitialData(filtered);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar datos');
    }
  };

  const loadInitialData = async (snapshotsData) => {
    try {
      const token = localStorage.getItem('token');
      
      console.log('📊 Cargando datos iniciales...', snapshotsData.length, 'snapshots');
      
      // Usar solo el snapshot más reciente para cargar uniones y clubs iniciales
      if (snapshotsData.length === 0) {
        console.warn('⚠️ No hay snapshots disponibles');
        return;
      }
      
      const latestSnapshot = snapshotsData[0]; // Ya están ordenados por fecha_importacion desc
      console.log('📅 Snapshot más reciente:', latestSnapshot.fecha, latestSnapshot.snapshot_id);
      
      // Cargar uniones del snapshot más reciente
      const unionesRes = await axios.get(
        `${API}/superunion/uniones?snapshot_id=${latestSnapshot.snapshot_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const unionesPPSR = unionesRes.data.uniones.filter(u => u.tipo === tipo);
      console.log('🔷 Uniones cargadas:', unionesPPSR.length);
      setAllUniones(unionesPPSR);
      
      // Cargar clubs del snapshot más reciente
      const clubsRes = await axios.get(
        `${API}/superunion/clubs?snapshot_id=${latestSnapshot.snapshot_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const clubsPPSR = clubsRes.data.clubs.filter(c => c.tipo === tipo);
      console.log('🏢 Clubs cargados:', clubsPPSR.length);
      setAllClubs(clubsPPSR);
      
      // Inicializar clubs visibles con todos los clubs
      setClubs(clubsPPSR.sort((a, b) => a.club_nombre.localeCompare(b.club_nombre)));
      
      // Modalidades
      const modalidadesSet = new Set(latestSnapshot.modalidades_ppsr || []);
      console.log('🎮 Modalidades:', Array.from(modalidadesSet));
      setModalidades(Array.from(modalidadesSet));
      
      toast.success(`✅ Datos cargados: ${unionesPPSR.length} uniones, ${clubsPPSR.length} clubs`);
      
    } catch (error) {
      console.error('❌ Error cargando datos iniciales:', error);
      toast.error('Error al cargar datos iniciales');
    }
  };

  const buscarPPSRPorClub = async () => {
    if (!busquedaClubIdPPSR.trim()) { toast.error('Ingrese un ID de club'); return; }
    setLoadingBusquedaPPSR(true);
    setResultadoClubPPSR(null);
    setResultadoUnionPPSR(null);
    try {
      const token = localStorage.getItem('token');
      const params = { club_id: busquedaClubIdPPSR.trim() };
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      const res = await axios.get(`${API}/superunion/ppsr/buscar-club`, {
        headers: { Authorization: `Bearer ${token}` }, params
      });
      if (res.data.success) {
        setResultadoClubPPSR(res.data);
        setHasFilters(true);
        toast.success(`Club ${res.data.nombre_club}: ${res.data.jugadores_activos} jugadores activos, ${res.data.mesas_unicas} mesas`);
      }
    } catch (e) { toast.error('Error: ' + (e.response?.data?.detail || e.message)); }
    finally { setLoadingBusquedaPPSR(false); }
  };

  const buscarPPSRPorUnion = async () => {
    if (!busquedaUnionIdPPSR.trim()) { toast.error('Ingrese un ID de union'); return; }
    setLoadingBusquedaPPSR(true);
    setResultadoClubPPSR(null);
    setResultadoUnionPPSR(null);
    try {
      const token = localStorage.getItem('token');
      const params = { union_id: busquedaUnionIdPPSR.trim() };
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      const res = await axios.get(`${API}/superunion/ppsr/buscar-union`, {
        headers: { Authorization: `Bearer ${token}` }, params
      });
      if (res.data.success) {
        setResultadoUnionPPSR(res.data);
        setHasFilters(true);
        toast.success(`Union ${busquedaUnionIdPPSR}: ${res.data.total_jugadores_activos} jugadores en ${res.data.total_clubs} clubs`);
      }
    } catch (e) { toast.error('Error: ' + (e.response?.data?.detail || e.message)); }
    finally { setLoadingBusquedaPPSR(false); }
  };

  const exportarIdsPPSR = async (tipoExp, idValor) => {
    try {
      const token = localStorage.getItem('token');
      const params = { tipo: tipoExp, id_valor: idValor };
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      const res = await axios.get(`${API}/superunion/ppsr/exportar-ids-jugadores`, {
        headers: { Authorization: `Bearer ${token}` }, params, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ppsr_${tipoExp}_${idValor}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel descargado');
    } catch (e) { toast.error('Error al exportar'); }
  };

  const applyFilters = async () => {
    // SI HAY BÚSQUEDA POR JUGADOR: Ignorar TODOS los filtros y cargar TODO el historial
    if (searchQuery && searchQuery.trim()) {
      setLoading(true);
      setHasFilters(true);
      
      try {
        const token = localStorage.getItem('token');
        
        // Construir URL con filtros de fecha si están disponibles
        let searchUrl = `${API}/superunion/buscar-jugador-global/${searchQuery.trim()}`;
        const searchParams = new URLSearchParams();
        if (fechaDesde) searchParams.append('fecha_desde', fechaDesde);
        if (fechaHasta) searchParams.append('fecha_hasta', fechaHasta);
        if (searchParams.toString()) searchUrl += `?${searchParams.toString()}`;
        
        const rangoTexto = (fechaDesde && fechaHasta) 
          ? `del ${fechaDesde} al ${fechaHasta}` 
          : 'TODO el historial';
        console.log(`🔍 Búsqueda de jugador: ${searchQuery} (${rangoTexto})`);
        toast.info(`🔍 Buscando ${rangoTexto} del jugador ${searchQuery}...`);
        
        const response = await axios.get(
          searchUrl,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (!response.data.success || response.data.registros.length === 0) {
          toast.warning(`No se encontraron registros para el jugador "${searchQuery}"`);
          setJugadores([]);
          setLoading(false);
          return;
        }
        
        const todosLosJugadores = response.data.registros;
        
        // Capturar info de agencia si viene del backend
        if (response.data.agencia_info) {
          setAgenciaInfo(response.data.agencia_info);
        } else {
          setAgenciaInfo(null);
        }
        setAgenciasList(response.data.agencias || []);
        
        console.log(`✅ Total jugadores encontrados: ${todosLosJugadores.length}`);
        
        // Agrupar por clubs para el panel lateral
        const clubsMap = {};
        todosLosJugadores.forEach(j => {
          const clubKey = j.club_nombre || 'SIN CLUB';
          if (!clubsMap[clubKey]) {
            clubsMap[clubKey] = {
              club_nombre: clubKey,
              union_nombre: j.union_nombre || 'SIN UNIÓN',
              jugadores: []
            };
          }
        });
        setClubs(Object.values(clubsMap));
        
        setJugadores(todosLosJugadores);
        toast.success(`✅ Encontrados ${todosLosJugadores.length} registros del jugador "${searchQuery}"`);
        
        setLoading(false);
        
        // Scroll automático a la tabla
        setTimeout(() => {
          const tabla = document.querySelector('table');
          if (tabla) {
            tabla.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 500);
        
        return;
      } catch (error) {
        console.error('❌ Error buscando jugador:', error);
        toast.error('Error buscando jugador: ' + (error.response?.data?.detail || error.message));
        setLoading(false);
        return;
      }
    }
    
    // FLUJO NORMAL: Con filtros de fecha y club
    // Limpiar agencia cuando no es busqueda por jugador
    setAgenciaInfo(null);
    setAgenciasList([]);
    
    if (!fechaDesde || !fechaHasta) {
      toast.error('Por favor selecciona un rango de fechas');
      return;
    }
    
    if (!selectedClubId) {
      toast.error('Por favor selecciona un club');
      return;
    }
    
    setLoading(true);
    setHasFilters(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Obtener snapshots en el rango de fechas
      const snapshotsEnRango = snapshots.filter(s => {
        return s.fecha >= fechaDesde && s.fecha <= fechaHasta;
      });
      
      console.log(`📊 Snapshots en rango ${fechaDesde} - ${fechaHasta}:`, snapshotsEnRango.length);
      
      if (snapshotsEnRango.length === 0) {
        toast.warning('No hay datos en el rango de fechas seleccionado');
        setJugadores([]);
        setLoading(false);
        return;
      }
      
      // Deduplicar: usar solo el snapshot más reciente de cada fecha
      const snapshotsPorFecha = {};
      snapshotsEnRango.forEach(snap => {
        const fecha = snap.fecha;
        if (!snapshotsPorFecha[fecha] || snap.fecha_importacion > snapshotsPorFecha[fecha].fecha_importacion) {
          snapshotsPorFecha[fecha] = snap;
        }
      });
      
      const snapshotsUnicos = Object.values(snapshotsPorFecha);
      console.log(`📊 Snapshots únicos (más recientes): ${snapshotsUnicos.length}`);
      
      // Cargar jugadores solo de snapshots únicos
      let todosLosJugadores = [];
      
      for (const snap of snapshotsUnicos) {
        let url = `${API}/superunion/jugadores?snapshot_id=${snap.snapshot_id}&club_id=${selectedClubId}&limit=10000`;
        if (searchQuery) {
          url += `&search=${searchQuery}`;
        }
        
        console.log(`📥 Cargando jugadores del snapshot ${snap.fecha} (${snap.snapshot_id.substring(0, 8)}...)...`);
        
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Agregar fecha al jugador para referencia
        const jugadoresConFecha = response.data.jugadores.map(j => ({
          ...j,
          fecha_snapshot: snap.fecha
        }));
        
        todosLosJugadores = todosLosJugadores.concat(jugadoresConFecha);
      }
      
      console.log(`✅ Total jugadores cargados: ${todosLosJugadores.length}`);
      
      // Filtrar por modalidades si hay alguna seleccionada
      if (selectedModalidades.length > 0) {
        todosLosJugadores = todosLosJugadores.filter(j => selectedModalidades.includes(j.modalidad));
        console.log(`🎮 Filtrados por modalidades [${selectedModalidades.join(', ')}]: ${todosLosJugadores.length}`);
      }
      
      // Ordenar por ganancia (mayor a menor)
      todosLosJugadores.sort((a, b) => b.ganancia - a.ganancia);
      
      setJugadores(todosLosJugadores);
      toast.success(`✅ Cargados ${todosLosJugadores.length} registros`);
      
    } catch (error) {
      console.error('❌ Error aplicando filtros:', error);
      toast.error('Error al cargar datos: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    if (jugadores.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }
    
    // Si es búsqueda de jugador, usar endpoint profesional del backend
    if (searchQuery && searchQuery.trim()) {
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ player_id: searchQuery.trim() });
        if (fechaDesde) params.append('fecha_desde', fechaDesde);
        if (fechaHasta) params.append('fecha_hasta', fechaHasta);
        
        toast.info('Generando Excel profesional...');
        
        const response = await axios.get(
          `${API}/superunion/ppsr/exportar-jugador-excel?${params.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `ppsr_jugador_${searchQuery.trim()}_${fechaDesde || 'inicio'}_${fechaHasta || 'fin'}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success('Excel descargado correctamente');
        return;
      } catch (error) {
        console.error('Error exportando Excel PPSR:', error);
        toast.error('Error al generar Excel');
        return;
      }
    }
    
    // Fallback: CSV para búsquedas por club (sin jugador específico)
    const formatearNumero = (num) => {
      return typeof num === 'number' ? num.toFixed(2) : '0.00';
    };
    
    const headers = ['Fecha', 'Juego', 'Mesa N°', 'ID Jugador', 'Apodo', 'Stack', 'Manos', 'Cuota', 'Ganancias', 'Total'];
    const rows = jugadores.map(j => [
      j.fecha_snapshot || '',
      j.modalidad || '',
      j.partida_id || '',
      j.jugador_id,
      j.jugador_nombre,
      formatearNumero(j.stack || 0),
      j.manos_jugadas || 0,
      formatearNumero(j.cuota || 0),
      formatearNumero(j.ganancia || 0),
      formatearNumero((j.ganancia || 0) + (j.cuota || 0))
    ]);
    
    let csv = headers.join(',') + '\n';
    rows.forEach(row => {
      csv += row.join(',') + '\n';
    });
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `ppsr_informe_${fechaDesde}_${fechaHasta}.csv`;
    link.click();
    
    toast.success('Archivo exportado');
  };

  const verJugadoresMesa = async (partida_id) => {
    if (!partida_id || partida_id === '-') {
      toast.error('ID de mesa no disponible');
      return;
    }
    
    setLoadingMesa(true);
    setMesaSeleccionada(partida_id);
    setShowMesaModal(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Llamar al endpoint que busca TODOS los jugadores de esta mesa
      // sin importar club o unión
      let url = `${API}/superunion/jugadores-mesa?partida_id=${partida_id}`;
      
      // Agregar rango de fechas si están disponibles
      if (fechaDesde && fechaHasta) {
        url += `&fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`;
      }
      
      console.log(`🎰 Buscando jugadores de mesa ${partida_id} (todos los clubs)...`);
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const jugadoresDeLaMesa = response.data.jugadores || [];
      setJugadoresMesa(jugadoresDeLaMesa);
      
      // Reset filtros del modal
      setFiltroClubMesa('');
      setFiltroJugadorMesa('');
      
      console.log(`🎰 Mesa ${partida_id}: ${jugadoresDeLaMesa.length} jugadores encontrados (todas las uniones)`);
      
      if (jugadoresDeLaMesa.length === 0) {
        toast.warning(`No se encontraron jugadores para la mesa ${partida_id}`);
      }
      
    } catch (error) {
      console.error('Error cargando jugadores de mesa:', error);
      toast.error('Error al cargar jugadores de la mesa');
      setJugadoresMesa([]);
    } finally {
      setLoadingMesa(false);
    }
  };

  const exportarMesaExcel = async () => {
    if (jugadoresMesa.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ partida_id: mesaSeleccionada });
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      
      toast.info('Generando Excel profesional...');
      
      const response = await axios.get(
        `${API}/superunion/ppsr/exportar-mesa-excel?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mesa_${mesaSeleccionada}_${jugadoresMesa.length}jugadores.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel descargado correctamente');
    } catch (error) {
      console.error('Error exportando Excel mesa:', error);
      toast.error('Error al generar Excel');
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0.00';
    return new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const ejecutarControlBot = async () => {
    setLoadingControlBot(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // 1. Obtener IDs ganadores nuevos (no en historial) - TODOS LOS DATOS HISTÓRICOS
      const response = await axios.get(
        `${API}/control-bot/ganadores-nuevos`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { ids_nuevos, total_ganadores, ya_enviados, pendientes, exportando } = response.data;
      
      if (ids_nuevos.length === 0) {
        toast.info('✅ No hay IDs ganadores nuevos. Todos ya fueron enviados.');
        setLoadingControlBot(false);
        return;
      }
      
      // ALERTA: Si hay más de 2000 IDs pendientes
      if (pendientes > 2000) {
        toast.warning(`⚠️ Atención: Hay ${pendientes} IDs pendientes. Exportando los primeros 2000.`);
      }
      
      // 2. Crear Excel con UNA SOLA COLUMNA (solo IDs)
      const datosExcel = ids_nuevos.map((id) => ({
        'Jugador ID': id
      }));
      
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(datosExcel);
      ws['!cols'] = [{ wch: 15 }];
      XLSX.utils.book_append_sheet(wb, ws, 'IDs Ganadores');
      
      // Nombre de archivo con fecha actual para control
      const fechaHoy = new Date().toISOString().split('T')[0];
      const nombreArchivo = `control_bot_ids_ganadores_${fechaHoy}.xlsx`;
      XLSX.writeFile(wb, nombreArchivo);
      
      // 3. Marcar IDs como enviados en el historial
      await axios.post(
        `${API}/control-bot/marcar-enviados`,
        { ids: ids_nuevos },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        `🤖 Control Bot: ${exportando} IDs ganadores exportados\n` +
        `Total ganadores: ${total_ganadores} | Ya enviados: ${ya_enviados} | Pendientes: ${pendientes}`
      );
      
      console.log(`🤖 Control Bot ejecutado: ${exportando} IDs exportados de ${pendientes} pendientes (Total histórico: ${total_ganadores} ganadores)`);
      
    } catch (error) {
      console.error('Error en Control Bot:', error);
      toast.error('Error ejecutando Control Bot: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingControlBot(false);
    }
  };

  const resetHistorialControlBot = async () => {
    if (!window.confirm('¿Estás seguro de resetear el historial? Esto hará que todos los IDs se exporten de nuevo.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API}/control-bot/reset-historial`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('✅ Historial reseteado correctamente');
      console.log('🤖 Control Bot: Historial reseteado');
      
    } catch (error) {
      console.error('Error reseteando historial:', error);
      toast.error('Error reseteando historial');
    }
  };

  // ============== ANÁLISIS DE RESULTADOS BOT ==============

  useEffect(() => {
    // Calcular semana anterior por defecto
    const hoy = new Date();
    const diaActual = hoy.getDay(); // 0=domingo, 1=lunes, etc
    const diasDesdeLunes = diaActual === 0 ? 6 : diaActual - 1;
    
    const lunesPasado = new Date(hoy);
    lunesPasado.setDate(hoy.getDate() - diasDesdeLunes - 7);
    
    const domingoPasado = new Date(lunesPasado);
    domingoPasado.setDate(lunesPasado.getDate() + 6);
    
    setSemanaInicioBot(lunesPasado.toISOString().split('T')[0]);
    setSemanaFinBot(domingoPasado.toISOString().split('T')[0]);
    
    loadHistorialInformes();
  }, []);

  const loadHistorialInformes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/bot-resultados/historial`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setHistorialInformes(response.data);
    } catch (error) {
      console.error('Error cargando historial de informes:', error);
    }
  };

  const handleArchivoResultadosChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Por favor selecciona un archivo Excel (.xlsx o .xls)');
        return;
      }
      setArchivoResultados(file);
      toast.info(`Archivo seleccionado: ${file.name}`);
    }
  };

  const procesarResultadosBot = async () => {
    if (!archivoResultados) {
      toast.error('Por favor selecciona un archivo Excel primero');
      return;
    }

    if (!semanaInicioBot || !semanaFinBot) {
      toast.error('Por favor selecciona el rango de semana');
      return;
    }

    setLoadingProcesar(true);

    try {
      const token = localStorage.getItem('token');
      
      // Crear FormData
      const formData = new FormData();
      formData.append('file', archivoResultados);
      formData.append('semana_inicio', semanaInicioBot);
      formData.append('semana_fin', semanaFinBot);
      formData.append('incluir_perdedores', incluyePerdedores);

      const response = await axios.post(
        `${API}/bot-resultados/procesar`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        const { archivos, stats } = response.data;
        
        if (archivos && archivos.length > 0) {
          toast.success(
            `✅ ${archivos.length} informes generados\n` +
            `Jugadores detectados: ${stats.jugadores_con_ganancia}\n` +
            `Uniones afectadas: ${stats.uniones_afectadas}`
          );
          
          // Recargar historial
          await loadHistorialInformes();
          
          // Limpiar archivo
          setArchivoResultados(null);
          document.getElementById('file-resultados-bot').value = '';
        } else {
          toast.info(response.data.message || 'No se generaron informes');
        }
      }
    } catch (error) {
      console.error('Error procesando resultados:', error);
      toast.error('Error procesando resultados: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingProcesar(false);
    }
  };

  const descargarInforme = async (historialId, union) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${API}/bot-resultados/descargar/${historialId}/${union}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Crear URL para descargar
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const fecha = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `informe_bot_retencion_${union}_${fecha}.xlsx`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`📥 Descargando informe de ${union}`);
    } catch (error) {
      console.error('Error descargando informe:', error);
      toast.error('Error descargando informe');
    }
  };

  const eliminarInforme = async (historialId) => {
    // Confirmar con el usuario
    if (!window.confirm('¿Estás seguro de eliminar este informe? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.delete(
        `${API}/bot-resultados/historial/${historialId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('🗑️ Informe eliminado correctamente');
        
        // Actualizar la lista de historial
        await loadHistorialInformes();
      }
    } catch (error) {
      console.error('Error eliminando informe:', error);
      toast.error('Error eliminando informe: ' + (error.response?.data?.detail || error.message));
    }
  };


  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {activeTab === 'informe' ? '💰 PPSR - Informe de Cash Games' : activeTab === 'chip-dumping' ? '🛡️ Alertas de Chip Dumping' : activeTab === 'collusion' ? '👥 Detector de Collusion' : '📋 Ejecutar Retencion'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {activeTab === 'informe' ? 'Análisis detallado de partidas en efectivo' : activeTab === 'chip-dumping' ? 'Detección de fraude entre uniones BR vs AR' : activeTab === 'collusion' ? 'Detecta teams que se sientan juntos a depredar recreacionales' : 'Retenciones por Chip Dumping confirmado por PPPoker'}
          </p>
        </div>
        
        {/* CONTROL BOT - Solo admin */}
        {activeTab === 'informe' && isAdmin && (<div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30 rounded-lg border-2 border-blue-300 dark:border-blue-600 p-4 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <Bot className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h3 className="font-bold text-blue-800 dark:text-blue-300">Control Bot</h3>
          </div>
          <p className="text-xs text-blue-700 dark:text-blue-400 mb-3">
            Exporta IDs ganadores no enviados (todos los datos históricos)
          </p>
          <div className="flex gap-2">
            <button
              onClick={ejecutarControlBot}
              disabled={loadingControlBot}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                loadingControlBot
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg'
              }`}
            >
              {loadingControlBot ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Procesando...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Ejecutar
                </>
              )}
            </button>
            <button
              onClick={resetHistorialControlBot}
              className="flex items-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
              title="Resetear historial de IDs enviados"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
          </div>
        </div>)}
      </div>


      {/* TABS DE NAVEGACION */}
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl" data-testid="ppsr-tabs">
        <button
          onClick={() => setActiveTab('informe')}
          data-testid="tab-informe-ppsr"
          className={`flex-1 px-6 py-2.5 font-bold text-sm rounded-lg transition-all ${
            activeTab === 'informe'
              ? 'bg-white dark:bg-gray-700 text-blue-700 dark:text-blue-400 shadow-md'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          Informe PPSR
        </button>
        <button
          onClick={() => setActiveTab('chip-dumping')}
          data-testid="tab-chip-dumping"
          className={`flex-1 px-6 py-2.5 font-bold text-sm rounded-lg transition-all ${
            activeTab === 'chip-dumping'
              ? 'bg-white dark:bg-gray-700 text-red-700 dark:text-red-400 shadow-md'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          Alertas Chip Dumping
        </button>
        <button
          onClick={() => setActiveTab('collusion')}
          data-testid="tab-collusion"
          className={`flex-1 px-6 py-2.5 font-bold text-sm rounded-lg transition-all ${
            activeTab === 'collusion'
              ? 'bg-white dark:bg-gray-700 text-purple-700 dark:text-purple-400 shadow-md'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          Collusion
        </button>
        <button
          onClick={() => setActiveTab('retencion')}
          data-testid="tab-retencion"
          className={`flex-1 px-6 py-2.5 font-bold text-sm rounded-lg transition-all ${
            activeTab === 'retencion'
              ? 'bg-white dark:bg-gray-700 text-red-700 dark:text-red-400 shadow-md'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          Retencion
        </button>
      </div>

      {activeTab === 'chip-dumping' && <ChipDumpingTab />}

      {activeTab === 'collusion' && <CollusionTab />}

      {activeTab === 'retencion' && <RetencionTab />}

      {activeTab === 'informe' && (<>
      {/* ANÁLISIS DE RESULTADOS BOT - Solo admin */}
      {isAdmin && (<div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 rounded-lg border-2 border-purple-300 dark:border-purple-600 p-4 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <FileSpreadsheet className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          <div>
            <h3 className="font-bold text-purple-800 dark:text-purple-300">Análisis de Resultados Bot</h3>
            <p className="text-xs text-purple-700 dark:text-purple-400">
              Procesa respuestas del bot y genera informes por unión
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Upload Excel */}
          <div>
            <label className="block text-sm font-semibold text-purple-800 dark:text-purple-300 mb-2">
              📤 Archivo de Resultados
            </label>
            <input
              id="file-resultados-bot"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleArchivoResultadosChange}
              className="w-full px-3 py-2 border-2 border-purple-300 dark:border-purple-600 rounded-lg text-sm"
            />
            {archivoResultados && (
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                ✓ {archivoResultados.name}
              </p>
            )}
          </div>
          
          {/* Fecha Inicio */}
          <div>
            <label className="block text-sm font-semibold text-purple-800 dark:text-purple-300 mb-2">
              📅 Semana Inicio
            </label>
            <input
              type="date"
              value={semanaInicioBot}
              onChange={(e) => setSemanaInicioBot(e.target.value)}
              className="w-full px-3 py-2 border-2 border-purple-300 dark:border-purple-600 rounded-lg"
            />
          </div>
          
          {/* Fecha Fin */}
          <div>
            <label className="block text-sm font-semibold text-purple-800 dark:text-purple-300 mb-2">
              📅 Semana Fin
            </label>
            <input
              type="date"
              value={semanaFinBot}
              onChange={(e) => setSemanaFinBot(e.target.value)}
              className="w-full px-3 py-2 border-2 border-purple-300 dark:border-purple-600 rounded-lg"
            />
          </div>
        </div>
        
        {/* Checkbox Incluir Perdedores */}
        <div className="mt-3 flex items-center gap-3">
          <input
            type="checkbox"
            id="incluir-perdedores"
            checked={incluyePerdedores}
            onChange={(e) => setIncluyePerdedores(e.target.checked)}
            className="w-5 h-5 text-purple-600 border-purple-300 rounded focus:ring-purple-500 cursor-pointer"
          />
          <label
            htmlFor="incluir-perdedores"
            className="text-sm font-semibold text-purple-800 dark:text-purple-300 cursor-pointer select-none"
          >
            📊 Incluir también jugadores con pérdidas (total negativo)
          </label>
        </div>
        {incluyePerdedores && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 ml-8">
            ⚠️ Se incluirán TODOS los jugadores detectados, incluso los que perdieron dinero
          </p>
        )}
        
        <button
          onClick={procesarResultadosBot}
          disabled={loadingProcesar || !archivoResultados}
          className={`mt-4 w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-bold transition-all ${
            loadingProcesar || !archivoResultados
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg hover:shadow-xl'
          }`}
        >
          {loadingProcesar ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Procesando...
            </>
          ) : (
            <>
              <FileSpreadsheet className="w-5 h-5" />
              Procesar y Generar Informes
            </>
          )}
        </button>
      </div>)}

      {/* HISTORIAL DE INFORMES GENERADOS */}
      {historialInformes.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">
            📊 Historial de Informes Generados
          </h3>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-purple-100 dark:bg-purple-900/30 border-b-2 border-purple-300 dark:border-purple-700">
                  <th className="px-4 py-3 text-left font-bold text-purple-800 dark:text-purple-300">Fecha</th>
                  <th className="px-4 py-3 text-left font-bold text-purple-800 dark:text-purple-300">Semana</th>
                  <th className="px-4 py-3 text-center font-bold text-purple-800 dark:text-purple-300">Positivos</th>
                  <th className="px-4 py-3 text-center font-bold text-purple-800 dark:text-purple-300">Uniones</th>
                  <th className="px-4 py-3 text-left font-bold text-purple-800 dark:text-purple-300">Descargar Informes</th>
                  <th className="px-4 py-3 text-center font-bold text-purple-800 dark:text-purple-300">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {historialInformes.map((hist, idx) => (
                  <tr key={idx} className="border-b border-gray-200 dark:border-gray-700 hover:bg-purple-50 dark:hover:bg-purple-900/20">
                    <td className="px-4 py-3">
                      {new Date(hist.fecha_generacion).toLocaleString('es-AR')}
                    </td>
                    <td className="px-4 py-3">
                      {new Date(hist.semana_inicio).toLocaleDateString('es-AR')} - {new Date(hist.semana_fin).toLocaleDateString('es-AR')}
                    </td>
                    <td className="px-4 py-3 text-center font-bold text-green-600 dark:text-green-400">
                      {hist.jugadores_con_ganancia}
                      {hist.incluyo_perdedores && (
                        <span className="ml-2 text-xs text-amber-600 dark:text-amber-400" title="Incluye perdedores">
                          📊
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center font-bold">
                      {hist.uniones_afectadas.length}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        {hist.archivos.map((archivo, aIdx) => (
                          <button
                            key={aIdx}
                            onClick={() => descargarInforme(hist.historial_id, archivo.union)}
                            className="flex items-center gap-1 px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-xs font-semibold transition-colors"
                            title={`${archivo.count} mesas, ${archivo.jugadores_unicos} jugadores`}
                          >
                            <Download className="w-3 h-3" />
                            {archivo.union}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => eliminarInforme(hist.historial_id)}
                        className="flex items-center justify-center gap-1 px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md text-xs font-semibold transition-colors mx-auto"
                        title="Eliminar informe"
                      >
                        <Trash2 className="w-3 h-3" />
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* BUSCADOR DE MESAS */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg shadow-lg border-2 border-green-200 dark:border-green-700 p-4">
        <div className="flex flex-col md:flex-row gap-3 items-end">
          <div className="flex-1">
            <label className="block text-sm font-bold text-green-800 dark:text-green-300 mb-2">
              🎰 Búsqueda Rápida de Mesa
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-600 dark:text-green-400" />
              <input
                type="text"
                value={busquedaMesaId}
                onChange={(e) => setBusquedaMesaId(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && busquedaMesaId.trim()) {
                    verJugadoresMesa(busquedaMesaId.trim());
                  }
                }}
                placeholder="Ingresa ID de mesa (ej: 123456789012-12345678) y presiona Enter"
                className="w-full pl-11 pr-3 py-3 border-2 border-green-300 dark:border-green-600 dark:bg-gray-800 rounded-lg focus:ring-2 focus:ring-green-500 text-sm font-mono"
              />
            </div>
            <p className="text-xs text-green-700 dark:text-green-400 mt-1">
              💡 Ingresa el ID completo de la mesa y presiona Enter para ver todos sus jugadores
            </p>
          </div>
          <button
            onClick={() => {
              if (busquedaMesaId.trim()) {
                verJugadoresMesa(busquedaMesaId.trim());
              } else {
                toast.error('Por favor ingresa un ID de mesa');
              }
            }}
            className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2"
          >
            <Search className="w-5 h-5" />
            Buscar Mesa
          </button>
        </div>
      </div>

      {/* MEGA FILTROS */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <h2 className="text-lg font-bold mb-4">🔍 Filtros</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Fecha Desde */}
          <div>
            <label className="block text-sm font-medium mb-1">Fecha Desde:</label>
            <input
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-violet-500"
            />
          </div>

          {/* Fecha Hasta */}
          <div>
            <label className="block text-sm font-medium mb-1">Fecha Hasta:</label>
            <input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-violet-500"
            />
          </div>

          {/* Unión */}
          <div>
            <label className="block text-sm font-medium mb-1">Unión:</label>
            <select
              value={selectedUnionId}
              onChange={(e) => {
                const unionId = e.target.value;
                setSelectedUnionId(unionId);
                setSelectedClubId(''); // Reset club al cambiar unión
                
                // Filtrar clubs por unión
                if (unionId) {
                  console.log(`🔷 Total allClubs disponibles: ${allClubs.length}`);
                  console.log(`🔷 Filtrando para unión: ${unionId} (tipo: ${typeof unionId})`);
                  
                  // Debug: ver tipos de union_id en allClubs
                  const sampleClub = allClubs[0];
                  if (sampleClub) {
                    console.log(`🔷 Ejemplo club union_id: ${sampleClub.union_id} (tipo: ${typeof sampleClub.union_id})`);
                  }
                  
                  let matchCount = 0;
                  const filteredClubs = allClubs.filter(c => {
                    const matches = c.union_id === unionId || String(c.union_id) === String(unionId);
                    if (matches && matchCount < 3) {
                      console.log(`   ✅ Match #${matchCount + 1}: ${c.club_nombre} (union_id: ${c.union_id})`);
                      matchCount++;
                    }
                    return matches;
                  });
                  
                  console.log(`🔷 Total clubs filtrados: ${filteredClubs.length}`);
                  setClubs(filteredClubs.sort((a, b) => a.club_nombre.localeCompare(b.club_nombre)));
                } else {
                  console.log(`🔷 Sin filtro de unión, mostrando todos: ${allClubs.length}`);
                  setClubs(allClubs.sort((a, b) => a.club_nombre.localeCompare(b.club_nombre)));
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-violet-500"
            >
              <option value="">Todas las uniones</option>
              {allUniones.map(u => (
                <option key={u.union_id} value={u.union_id}>
                  Unión {u.union_id} ({u.total_clubs} clubs)
                </option>
              ))}
            </select>
          </div>

          {/* Club */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Club: {clubs.length > 0 && <span className="text-violet-600">({clubs.length} disponibles)</span>}
            </label>
            <select
              value={selectedClubId}
              onChange={(e) => setSelectedClubId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-violet-500"
              disabled={clubs.length === 0}
            >
              <option value="">Selecciona un club</option>
              {clubs.map(c => (
                <option key={c.club_id} value={c.club_id}>
                  {c.club_nombre} ({c.total_jugadores} jugadores)
                </option>
              ))}
            </select>
            {clubs.length === 0 && selectedUnionId && (
              <p className="text-xs text-amber-600 mt-1">⚠️ No hay clubs para esta unión</p>
            )}
          </div>

          {/* Modalidad - Checkboxes */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-2">Modalidades:</label>
            <div className="flex flex-wrap gap-2">
              {modalidades.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">Carga datos para ver modalidades</p>
              ) : (
                <>
                  {modalidades.map(mod => (
                    <label
                      key={mod}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedModalidades.includes(mod)
                          ? 'bg-violet-100 dark:bg-violet-900 border-violet-500 text-violet-800 dark:text-violet-200'
                          : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-violet-400 dark:hover:border-violet-500'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedModalidades.includes(mod)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedModalidades([...selectedModalidades, mod]);
                          } else {
                            setSelectedModalidades(selectedModalidades.filter(m => m !== mod));
                          }
                        }}
                        className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500"
                      />
                      <span className="text-sm font-medium">{mod}</span>
                    </label>
                  ))}
                  {selectedModalidades.length > 0 && (
                    <button
                      onClick={() => setSelectedModalidades([])}
                      className="px-3 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors"
                    >
                      Limpiar
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Búsqueda Rápida */}
          <div className="md:col-span-3">
            <label className="block text-sm font-medium mb-1">Busqueda Rapida:</label>
            <div className="flex gap-1 mb-2">
              {['jugador', 'club', 'union'].map(t => (
                <button key={t} onClick={() => setTipoBusquedaPPSR(t)}
                  className={`px-3 py-1 rounded-lg font-semibold text-xs transition-all ${
                    tipoBusquedaPPSR === t
                      ? (t === 'jugador' ? 'bg-violet-600 text-white' : t === 'club' ? 'bg-blue-600 text-white' : 'bg-emerald-600 text-white')
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>
                  {t === 'jugador' ? 'ID Jugador' : t === 'club' ? 'ID Club' : 'ID Union'}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              {tipoBusquedaPPSR === 'jugador' && (
                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Nombre o ID del jugador..." className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-violet-500" />
              )}
              {tipoBusquedaPPSR === 'club' && (
                <>
                  <input type="text" value={busquedaClubIdPPSR} onChange={(e) => setBusquedaClubIdPPSR(e.target.value)}
                    placeholder="ID del club..." className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg" />
                  <button onClick={buscarPPSRPorClub} disabled={loadingBusquedaPPSR}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold text-sm">
                    {loadingBusquedaPPSR ? 'Buscando...' : 'Buscar'}
                  </button>
                </>
              )}
              {tipoBusquedaPPSR === 'union' && (
                <>
                  <input type="text" value={busquedaUnionIdPPSR} onChange={(e) => setBusquedaUnionIdPPSR(e.target.value)}
                    placeholder="ID de union..." className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg" />
                  <button onClick={buscarPPSRPorUnion} disabled={loadingBusquedaPPSR}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold text-sm">
                    {loadingBusquedaPPSR ? 'Buscando...' : 'Buscar'}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Botón Cargar Datos */}
          <div className="flex items-end">
            <button
              onClick={() => applyFilters()}
              disabled={loading || (!searchQuery && (!fechaDesde || !fechaHasta || !selectedClubId))}
              className={`w-full px-4 py-2.5 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                loading || (!searchQuery && (!fechaDesde || !fechaHasta || !selectedClubId))
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl'
              }`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Cargando...
                </>
              ) : (
                <>
                  <FileSpreadsheet className="w-5 h-5" />
                  Cargar Datos
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Info de estado */}
        {!fechaDesde || !fechaHasta || !selectedClubId ? (
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              ℹ️ <strong>Para cargar datos:</strong> {searchQuery ? 'La búsqueda de jugador respeta los filtros de fecha si están configurados' : 'Selecciona rango de fechas, unión (opcional) y club (obligatorio)'}
            </p>
          </div>
        ) : null}
      </div>

      {/* Resultado Club PPSR */}
      {resultadoClubPPSR && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-xl p-4 mb-4 shadow">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-blue-800 dark:text-blue-300 text-lg">
              Club: {resultadoClubPPSR.nombre_club} (ID: {resultadoClubPPSR.club_id})
            </h3>
            <button onClick={() => exportarIdsPPSR('club', resultadoClubPPSR.club_id)}
              className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700">
              <Download className="w-4 h-4" /> Exportar IDs
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
              <div className="text-2xl font-bold text-blue-600">{resultadoClubPPSR.jugadores_activos}</div>
              <div className="text-xs text-gray-500">Jugadores Activos</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
              <div className="text-2xl font-bold text-purple-600">{resultadoClubPPSR.mesas_unicas}</div>
              <div className="text-xs text-gray-500">Mesas Unicas</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
              <div className={`text-2xl font-bold ${resultadoClubPPSR.total_ganancia >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {resultadoClubPPSR.total_ganancia?.toLocaleString('es-AR', {minimumFractionDigits: 2})}
              </div>
              <div className="text-xs text-gray-500">Ganancia Total</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
              <div className="text-2xl font-bold text-orange-600">
                {resultadoClubPPSR.total_cuota?.toLocaleString('es-AR', {minimumFractionDigits: 2})}
              </div>
              <div className="text-xs text-gray-500">Cuota Total</div>
            </div>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0">
                <tr className="bg-blue-100 dark:bg-blue-800">
                  <th className="px-3 py-2 text-left">ID</th>
                  <th className="px-3 py-2 text-left">Nombre</th>
                  <th className="px-3 py-2 text-right">Mesas</th>
                  <th className="px-3 py-2 text-right">Ganancia</th>
                  <th className="px-3 py-2 text-right">Cuota</th>
                  <th className="px-3 py-2 text-right">Manos</th>
                </tr>
              </thead>
              <tbody>
                {resultadoClubPPSR.jugadores?.map((j, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}>
                    <td className="px-3 py-1.5 font-mono font-bold">{j.jugador_id}</td>
                    <td className="px-3 py-1.5">{j.jugador_nombre || '-'}</td>
                    <td className="px-3 py-1.5 text-right">{j.mesas_jugadas}</td>
                    <td className={`px-3 py-1.5 text-right font-semibold ${j.ganancia_total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {j.ganancia_total?.toLocaleString('es-AR', {minimumFractionDigits: 2})}
                    </td>
                    <td className="px-3 py-1.5 text-right">{j.cuota_total?.toLocaleString('es-AR', {minimumFractionDigits: 2})}</td>
                    <td className="px-3 py-1.5 text-right">{j.manos_total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Resultado Union PPSR */}
      {resultadoUnionPPSR && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 rounded-xl p-4 mb-4 shadow">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-emerald-800 dark:text-emerald-300 text-lg">
              Union ID: {resultadoUnionPPSR.union_id}
            </h3>
            <button onClick={() => exportarIdsPPSR('union', resultadoUnionPPSR.union_id)}
              className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700">
              <Download className="w-4 h-4" /> Exportar IDs Union
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
              <div className="text-2xl font-bold text-emerald-600">{resultadoUnionPPSR.total_jugadores_activos}</div>
              <div className="text-xs text-gray-500">Jugadores Activos</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
              <div className="text-2xl font-bold text-blue-600">{resultadoUnionPPSR.total_clubs}</div>
              <div className="text-xs text-gray-500">Clubs</div>
            </div>
            <div className={`bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm`}>
              <div className={`text-2xl font-bold ${resultadoUnionPPSR.total_ganancia >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {resultadoUnionPPSR.total_ganancia?.toLocaleString('es-AR', {minimumFractionDigits: 2})}
              </div>
              <div className="text-xs text-gray-500">Ganancia Total</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
              <div className="text-2xl font-bold text-orange-600">
                {resultadoUnionPPSR.total_cuota?.toLocaleString('es-AR', {minimumFractionDigits: 2})}
              </div>
              <div className="text-xs text-gray-500">Cuota Total</div>
            </div>
          </div>
          <div className="space-y-3">
            {resultadoUnionPPSR.clubs?.map((club, ci) => (
              <div key={ci} className="bg-white dark:bg-gray-800 border rounded-lg p-3 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-800 dark:text-white">{club.nombre_club}</span>
                    <span className="text-xs text-gray-500">(ID: {club.club_id})</span>
                    <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded">
                      {club.jugadores_activos} activos
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className={`font-semibold ${club.total_ganancia >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      Gan: {club.total_ganancia?.toLocaleString('es-AR', {minimumFractionDigits: 2})}
                    </span>
                    <button onClick={() => exportarIdsPPSR('club', club.club_id)}
                      className="text-blue-600 hover:text-blue-800 font-semibold text-xs flex items-center gap-1">
                      <Download className="w-3 h-3" /> Excel
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto max-h-60 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0">
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        <th className="px-2 py-1 text-left">ID</th>
                        <th className="px-2 py-1 text-left">Nombre</th>
                        <th className="px-2 py-1 text-right">Mesas</th>
                        <th className="px-2 py-1 text-right">Ganancia</th>
                        <th className="px-2 py-1 text-right">Cuota</th>
                      </tr>
                    </thead>
                    <tbody>
                      {club.jugadores?.slice(0, 20).map((j, ji) => (
                        <tr key={ji} className={ji % 2 === 0 ? '' : 'bg-gray-50 dark:bg-gray-700'}>
                          <td className="px-2 py-1 font-mono font-bold">{j.jugador_id}</td>
                          <td className="px-2 py-1">{j.jugador_nombre || '-'}</td>
                          <td className="px-2 py-1 text-right">{j.mesas_jugadas}</td>
                          <td className={`px-2 py-1 text-right font-semibold ${j.ganancia_total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {j.ganancia_total?.toLocaleString('es-AR', {minimumFractionDigits: 2})}
                          </td>
                          <td className="px-2 py-1 text-right">{j.cuota_total?.toLocaleString('es-AR', {minimumFractionDigits: 2})}</td>
                        </tr>
                      ))}
                      {club.jugadores?.length > 20 && (
                        <tr><td colSpan="5" className="px-2 py-1 text-center text-gray-500 italic">
                          ...y {club.jugadores.length - 20} mas (descargar Excel)
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CONTENIDO: JUGADORES + CLUBS (lado derecho) */}
      {hasFilters && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,300px] gap-4">
          {/* IZQUIERDA: JUGADORES */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow order-2 lg:order-1">
            <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                👥 Jugadores ({jugadores.length})
                {selectedClubId && ` - ${clubs.find(c => c.club_id === selectedClubId)?.club_nombre}`}
              </h2>
              {jugadores.length > 0 && (
                <button
                  onClick={exportToExcel}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Exportar XLS
                </button>
              )}
            </div>
            <div className="p-4">
              {/* Ficha del jugador con boton COPY */}
              {searchQuery && jugadores.length > 0 && (
                <div className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 p-4 mb-4 rounded-lg border border-amber-300 dark:border-amber-600">
                  <div className="flex justify-between items-start">
                    <div className="space-y-1.5 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-amber-700 dark:text-amber-300 font-semibold text-xs w-28">ID Jugador:</span>
                        <span className="font-bold text-sm text-amber-900 dark:text-amber-100">{searchQuery}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-amber-700 dark:text-amber-300 font-semibold text-xs w-28">Apodo:</span>
                        <span className="font-bold text-sm text-amber-900 dark:text-amber-100">{agenciasList.length > 0 ? agenciasList[0].jugador_name : (jugadores[0]?.jugador_nombre || '-')}</span>
                      </div>
                      {agenciasList.length > 0 ? agenciasList.map((ag, idx) => (
                        <div key={idx} className={idx > 0 ? 'pt-2 mt-2 border-t border-amber-200 dark:border-amber-700' : ''}>
                          <div className="flex items-center gap-2">
                            <span className="text-amber-700 dark:text-amber-300 font-semibold text-xs w-28">Agencia:</span>
                            <span className="font-bold text-sm text-amber-900 dark:text-amber-100">{ag.agencia_name} (ID: {ag.agencia_id})</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-amber-700 dark:text-amber-300 font-semibold text-xs w-28">Club:</span>
                            <span className="font-bold text-sm text-amber-900 dark:text-amber-100">{ag.club_name} (ID: {ag.club_id})</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-amber-700 dark:text-amber-300 font-semibold text-xs w-28">ID Union:</span>
                            <span className="font-bold text-sm text-amber-900 dark:text-amber-100">{ag.union_id || '-'}</span>
                          </div>
                        </div>
                      )) : (() => {
                        const clubsMap = {};
                        jugadores.forEach(j => {
                          const cid = j.club_id || j.club_nombre;
                          if (cid && !clubsMap[cid]) {
                            clubsMap[cid] = { club_id: j.club_id, club_nombre: j.club_nombre, union_id: j.union_id };
                          }
                        });
                        const clubsList = Object.values(clubsMap);
                        return clubsList.map((c, idx) => (
                          <div key={idx} className={idx > 0 ? 'pt-2 mt-2 border-t border-amber-200 dark:border-amber-700' : ''}>
                            <div className="flex items-center gap-2">
                              <span className="text-amber-700 dark:text-amber-300 font-semibold text-xs w-28">Club:</span>
                              <span className="font-bold text-sm text-amber-900 dark:text-amber-100">{c.club_nombre} (ID: {c.club_id})</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-amber-700 dark:text-amber-300 font-semibold text-xs w-28">ID Union:</span>
                              <span className="font-bold text-sm text-amber-900 dark:text-amber-100">{c.union_id || '-'}</span>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                    <button
                      onClick={() => {
                        const apodo = agenciasList.length > 0 ? agenciasList[0].jugador_name : (jugadores[0]?.jugador_nombre || '-');
                        let texto = `ID Jugador: ${searchQuery}\nApodo: ${apodo}`;
                        if (agenciasList.length > 0) {
                          agenciasList.forEach(ag => {
                            texto += `\nAgencia: ${ag.agencia_name} (ID: ${ag.agencia_id})`;
                            texto += `\nClub: ${ag.club_name} (ID: ${ag.club_id})`;
                            texto += `\nID Union: ${ag.union_id || '-'}`;
                          });
                        } else {
                          const clubsMap = {};
                          jugadores.forEach(j => {
                            const cid = j.club_id || j.club_nombre;
                            if (cid && !clubsMap[cid]) {
                              clubsMap[cid] = { club_id: j.club_id, club_nombre: j.club_nombre, union_id: j.union_id };
                            }
                          });
                          Object.values(clubsMap).forEach(c => {
                            texto += `\nClub: ${c.club_nombre} (ID: ${c.club_id})`;
                            texto += `\nID Union: ${c.union_id || '-'}`;
                          });
                        }
                        navigator.clipboard.writeText(texto);
                        toast.success('Datos copiados al portapapeles');
                      }}
                      className="ml-3 flex items-center gap-1.5 px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors text-xs font-semibold shrink-0"
                      title="Copiar datos para WhatsApp"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                      Copiar
                    </button>
                  </div>
                </div>
              )}
              {(!selectedClubId && !searchQuery) ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  👉 Selecciona un club en el panel derecho
                </div>
              ) : loading ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">Cargando...</div>
              ) : jugadores.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  No hay jugadores con los filtros seleccionados
                </div>
              ) : (
                <div className="overflow-x-auto">
                  {/* Fila de TOTALES */}
                  {jugadores.length > 0 && (
                    <div className="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-gray-700 dark:to-gray-700 p-4 mb-4 rounded-lg border-2 border-violet-200 dark:border-violet-600">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Total Jugadores</p>
                          <p className="text-2xl font-bold text-violet-600 dark:text-violet-400">
                            {jugadores.length}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Total Manos</p>
                          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                            {jugadores.reduce((sum, j) => sum + (j.manos_jugadas || 0), 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Total Cuota</p>
                          <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                            {formatNumber(jugadores.reduce((sum, j) => sum + (j.cuota || 0), 0))}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Total Ganancias</p>
                          <p className={`text-2xl font-bold ${
                            jugadores.reduce((sum, j) => sum + (j.ganancia || 0), 0) >= 0 
                              ? 'text-green-600 dark:text-green-400' 
                              : 'text-red-600 dark:text-red-400'
                          }`}>
                            {formatNumber(jugadores.reduce((sum, j) => sum + (j.ganancia || 0), 0))}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-violet-100 to-purple-100 dark:from-gray-700 dark:to-gray-700">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider">#</th>
                        <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider">Fecha</th>
                        <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider">Juego</th>
                        <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider">Mesa N°</th>
                        <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider">ID Jugador</th>
                        <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider">Apodo</th>
                        <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider">Stack</th>
                        <th className="px-3 py-3 text-center text-xs font-bold uppercase tracking-wider">Manos</th>
                        <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider">Cuota</th>
                        <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider">Ganancias</th>
                        <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider">Total</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {jugadores.map((jugador, idx) => {
                        const total = (jugador.ganancia || 0) + (jugador.cuota || 0);
                        return (
                          <tr key={idx} className="hover:bg-violet-50 dark:hover:bg-gray-700 transition-colors">
                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {idx + 1}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              {jugador.fecha_snapshot || jugador.fecha || '-'}
                            </td>
                            <td className="px-3 py-3 text-sm">
                              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-md text-xs font-medium">
                                {jugador.modalidad || '-'}
                              </span>
                            </td>
                            <td className="px-2 py-3 text-xs font-mono break-all max-w-[120px]">
                              {jugador.partida_id && jugador.partida_id !== '-' ? (
                                <button
                                  onClick={() => verJugadoresMesa(jugador.partida_id)}
                                  className="text-violet-600 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300 underline hover:no-underline transition-colors cursor-pointer"
                                  title="Ver todos los jugadores de esta mesa"
                                >
                                  {jugador.partida_id}
                                </button>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 font-mono">
                              {jugador.jugador_id}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                              {jugador.jugador_nombre}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-semibold text-blue-600 dark:text-blue-400">
                              {formatNumber(jugador.stack || 0)}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-center text-sm text-gray-700 dark:text-gray-300 font-semibold">
                              {(jugador.manos_jugadas || 0).toLocaleString()}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-semibold text-orange-600 dark:text-orange-400">
                              {formatNumber(jugador.cuota)}
                            </td>
                            <td className={`px-3 py-3 whitespace-nowrap text-right text-sm font-bold ${
                              (jugador.ganancia || 0) < 0 
                                ? 'text-red-600 dark:text-red-400' 
                                : 'text-green-600 dark:text-green-400'
                            }`}>
                              {formatNumber(jugador.ganancia)}
                            </td>
                            <td className={`px-3 py-3 whitespace-nowrap text-right text-sm font-bold ${
                              total < 0 
                                ? 'text-red-700 dark:text-red-400' 
                                : 'text-green-700 dark:text-green-400'
                            }`}>
                              {formatNumber(total)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* DERECHA: LISTA DE CLUBS */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow order-1 lg:order-2 max-h-[800px] flex flex-col">
            <div className="p-3 border-b dark:border-gray-700 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-gray-700 dark:to-gray-700">
              <h2 className="text-base font-bold text-gray-900 dark:text-white">🏢 Clubs</h2>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                {searchQuery ? `Mostrando clubs del jugador ${searchQuery}` : `${clubs.length} disponibles${selectedUnionId ? ` (Unión ${selectedUnionId})` : ''}`}
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {searchQuery ? (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                  <p className="text-xs text-blue-800 dark:text-blue-300">
                    ℹ️ <strong>Búsqueda por jugador:</strong> La tabla muestra TODOS los registros del jugador en todos los clubs automáticamente. No necesitas seleccionar un club.
                  </p>
                </div>
              ) : clubs.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
                  No hay clubs disponibles
                </div>
              ) : (
                clubs.map((club) => (
                  <div
                    key={club.club_id}
                    onClick={() => setSelectedClubId(club.club_id)}
                    className={`p-2.5 rounded-lg cursor-pointer transition-all ${
                      selectedClubId === club.club_id
                        ? 'bg-gradient-to-r from-violet-100 to-purple-100 dark:from-violet-900/50 dark:to-purple-900/50 border-2 border-violet-500 shadow-md'
                        : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 border-2 border-transparent'
                    }`}
                  >
                    <div className="font-semibold text-xs text-gray-900 dark:text-white truncate" title={club.club_nombre}>
                      {club.club_nombre}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 flex justify-between items-center">
                      <span>👥 {club.total_jugadores}</span>
                      <span className={club.ganancia_total < 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-green-600 dark:text-green-400 font-semibold'}>
                        {formatNumber(club.ganancia_total)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {!hasFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
          <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h3 className="text-xl font-semibold mb-2">Selecciona un club para comenzar</h3>
          <p className="text-gray-600">
            Usa los filtros de arriba para seleccionar una unión y un club
          </p>
        </div>
      )}

      {/* MODAL: JUGADORES DE LA MESA */}
      {showMesaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
            {/* Header del Modal */}
            <div className="p-6 border-b dark:border-gray-700 flex justify-between items-center bg-gradient-to-r from-violet-50 to-purple-50 dark:from-gray-700 dark:to-gray-700">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  🎰 Mesa N° {mesaSeleccionada}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {jugadoresMesa.length} jugadores en esta partida
                </p>
              </div>
              <button
                onClick={() => {
                  setShowMesaModal(false);
                  setMesaSeleccionada(null);
                  setJugadoresMesa([]);
                }}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                title="Cerrar"
              >
                <X className="w-6 h-6 text-gray-600 dark:text-gray-300" />
              </button>
            </div>

            {/* Filtros y Exportar */}
            {!loadingMesa && jugadoresMesa.length > 0 && (
              <div className="px-6 py-4 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <div className="flex flex-wrap gap-3 items-center">
                  {/* Filtro Club */}
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={filtroClubMesa}
                        onChange={(e) => setFiltroClubMesa(e.target.value)}
                        placeholder="Filtrar por club..."
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded-lg focus:ring-2 focus:ring-violet-500 text-sm"
                      />
                    </div>
                  </div>

                  {/* Filtro Jugador */}
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={filtroJugadorMesa}
                        onChange={(e) => setFiltroJugadorMesa(e.target.value)}
                        placeholder="Filtrar por ID o Apodo..."
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded-lg focus:ring-2 focus:ring-violet-500 text-sm"
                      />
                    </div>
                  </div>

                  {/* Limpiar Filtros */}
                  {(filtroClubMesa || filtroJugadorMesa) && (
                    <button
                      onClick={() => {
                        setFiltroClubMesa('');
                        setFiltroJugadorMesa('');
                      }}
                      className="px-3 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors"
                    >
                      Limpiar
                    </button>
                  )}

                  {/* Botón Exportar Excel */}
                  <button
                    onClick={exportarMesaExcel}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Exportar Excel
                  </button>
                </div>
              </div>
            )}

            {/* Contenido del Modal */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingMesa ? (
                <div className="text-center py-12">
                  <div className="animate-spin h-12 w-12 border-4 border-violet-500 border-t-transparent rounded-full mx-auto"></div>
                  <p className="mt-4 text-gray-600 dark:text-gray-400">Cargando jugadores...</p>
                </div>
              ) : jugadoresMesa.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  No hay jugadores en esta mesa
                </div>
              ) : (
                <>
                  {/* Aplicar filtros */}
                  {(() => {
                    let jugadoresFiltrados = jugadoresMesa;
                    
                    if (filtroClubMesa) {
                      jugadoresFiltrados = jugadoresFiltrados.filter(j => 
                        j.club_nombre?.toLowerCase().includes(filtroClubMesa.toLowerCase())
                      );
                    }
                    
                    if (filtroJugadorMesa) {
                      jugadoresFiltrados = jugadoresFiltrados.filter(j => 
                        j.jugador_id?.toString().includes(filtroJugadorMesa) ||
                        j.jugador_nombre?.toLowerCase().includes(filtroJugadorMesa.toLowerCase())
                      );
                    }
                    
                    return (
                      <>
                        {/* Totales de la Mesa (basados en filtros) */}
                        <div className="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-gray-700 dark:to-gray-700 p-4 mb-6 rounded-lg border-2 border-violet-200 dark:border-violet-600">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center">
                              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                                Jugadores {(filtroClubMesa || filtroJugadorMesa) && `(${jugadoresFiltrados.length}/${jugadoresMesa.length})`}
                              </p>
                              <p className="text-xl font-bold text-violet-600 dark:text-violet-400">
                                {jugadoresFiltrados.length}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Total Manos</p>
                              <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                                {jugadoresFiltrados.reduce((sum, j) => sum + (j.manos_jugadas || 0), 0).toLocaleString()}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Total Cuota</p>
                              <p className="text-xl font-bold text-orange-600 dark:text-orange-400">
                                {formatNumber(jugadoresFiltrados.reduce((sum, j) => sum + (j.cuota || 0), 0))}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Total Ganancias</p>
                              <p className={`text-xl font-bold ${
                                jugadoresFiltrados.reduce((sum, j) => sum + (j.ganancia || 0), 0) >= 0 
                                  ? 'text-green-600 dark:text-green-400' 
                                  : 'text-red-600 dark:text-red-400'
                              }`}>
                                {formatNumber(jugadoresFiltrados.reduce((sum, j) => sum + (j.ganancia || 0), 0))}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Tabla de Jugadores */}
                        {jugadoresFiltrados.length === 0 ? (
                          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                            No hay jugadores que coincidan con los filtros
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-gradient-to-r from-violet-100 to-purple-100 dark:from-gray-700 dark:to-gray-700">
                                <tr>
                                  <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider">Pos.</th>
                                  <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider">Club</th>
                                  <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider">ID Jugador</th>
                                  <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider">Apodo</th>
                                  <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider">Stack</th>
                                  <th className="px-3 py-3 text-center text-xs font-bold uppercase tracking-wider">Manos</th>
                                  <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider">Cuota</th>
                                  <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider">Ganancias</th>
                                  <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider">Total</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {jugadoresFiltrados.map((jugador, idx) => {
                                  const total = (jugador.ganancia || 0) + (jugador.cuota || 0);
                                  return (
                                    <tr key={idx} className="hover:bg-violet-50 dark:hover:bg-gray-700 transition-colors">
                                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        {idx + 1}
                                      </td>
                                      <td className="px-3 py-3 whitespace-nowrap text-xs">
                                        <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-md font-medium">
                                          {jugador.club_nombre || 'N/A'}
                                        </span>
                                      </td>
                                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 font-mono">
                                        {jugador.jugador_id}
                                      </td>
                                      <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {jugador.jugador_nombre}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-semibold text-blue-600 dark:text-blue-400">
                                {formatNumber(jugador.stack || 0)}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-center text-sm text-gray-700 dark:text-gray-300 font-semibold">
                                {(jugador.manos_jugadas || 0).toLocaleString()}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-semibold text-orange-600 dark:text-orange-400">
                                {formatNumber(jugador.cuota)}
                              </td>
                              <td className={`px-3 py-3 whitespace-nowrap text-right text-sm font-bold ${
                                (jugador.ganancia || 0) < 0 
                                  ? 'text-red-600 dark:text-red-400' 
                                  : 'text-green-600 dark:text-green-400'
                              }`}>
                                {formatNumber(jugador.ganancia)}
                              </td>
                              <td className={`px-3 py-3 whitespace-nowrap text-right text-sm font-bold ${
                                total < 0 
                                  ? 'text-red-700 dark:text-red-400' 
                                  : 'text-green-700 dark:text-green-400'
                              }`}>
                                {formatNumber(total)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            );
          })()}
        </>
      )}
    </div>

            {/* Footer del Modal */}
            <div className="p-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex justify-between items-center">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Mesa: <span className="font-mono font-semibold">{mesaSeleccionada}</span>
              </div>
              <button
                onClick={() => {
                  setShowMesaModal(false);
                  setMesaSeleccionada(null);
                  setJugadoresMesa([]);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-semibold transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Volver
              </button>
            </div>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}
